import React, { useState, useEffect, useCallback, useMemo } from 'react'
import { Card, Select, Button, Row, Col, Space, Input, Empty, Spin, message } from 'antd'
import { PlusOutlined, PlayCircleOutlined, SaveOutlined, DeleteOutlined, RobotOutlined } from '@ant-design/icons'
import ListPageHeader from '@shared/ListPageHeader'
import { useTranslation } from 'react-i18next'

import { workflowService } from '@services/admin/workflowService'
import { orderService } from '@services/admin/orderService'
import { productService } from '@services/admin/productService'
import { inventoryService } from '@services/admin/inventoryService'
import { inboundService } from '@services/admin/inboundService'
import { shipmentService } from '@services/admin/shipmentService'
import { returnOrderService } from '@services/admin/returnOrderService'
import { inventorySnapshotService } from '@services/admin/inventorySnapshotService'
import { brandService } from '@services/admin/brandService'
import { salesChannelService } from '@services/admin/salesChannelService'
import { contactAddressService } from '@services/admin/contactAddressService'

import AIAssistantDrawer from './components/AIAssistantDrawer'
import CodeEditor from './components/CodeEditor'
import ExecutionPanel from './components/ExecutionPanel'
import { DEFAULT_WORKFLOW_SCRIPT } from './constants'

// All available services for workflow scripts
const workflowServices = {
  order: orderService,
  product: productService,
  inventory: inventoryService,
  inbound: inboundService,
  shipment: shipmentService,
  returnOrder: returnOrderService,
  inventorySnapshot: inventorySnapshotService,
  brand: brandService,
  salesChannel: salesChannelService,
  contactAddress: contactAddressService,
}

export default function WorkflowsIndex() {
  const { t } = useTranslation()
  const [workflows, setWorkflows] = useState([])
  const [selectedId, setSelectedId] = useState(null)
  const [currentWorkflow, setCurrentWorkflow] = useState(null)
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)

  // AI Assistant states
  const [aiDrawerVisible, setAiDrawerVisible] = useState(false)
  const [aiProcessing, setAiProcessing] = useState(false)

  // Fetch all workflows
  const fetchWorkflows = useCallback(async () => {
    setLoading(true)
    try {
      const res = await workflowService.fetchList()
      if (res.success) {
        setWorkflows(res.data || [])
      }
    } catch (error) {
      message.error(t('common.error.fetch_failed'))
    } finally {
      setLoading(false)
    }
  }, [t])

  // Fetch single workflow detail
  const fetchWorkflow = useCallback(async (id) => {
    if (!id) {
      setCurrentWorkflow(null)
      return
    }
    setLoading(true)
    try {
      const res = await workflowService.get(id)
      if (res.success) {
        setCurrentWorkflow(res.data)
      }
    } catch (error) {
      message.error(t('common.error.fetch_failed'))
    } finally {
      setLoading(false)
    }
  }, [t])

  useEffect(() => {
    fetchWorkflows()
  }, [fetchWorkflows])

  useEffect(() => {
    fetchWorkflow(selectedId)
  }, [selectedId, fetchWorkflow])

  // Create new workflow (local only, not saved yet)
  const handleCreate = () => {
    setSelectedId(null)
    setCurrentWorkflow({
      id: null,  // null means not saved yet
      name: t('workflows.new_workflow'),
      description: '',
      script: DEFAULT_WORKFLOW_SCRIPT,
      ui_config: { inputs: [] }
    })
  }

  // Save current workflow (create or update)
  const handleSave = async () => {
    if (!currentWorkflow) return
    setSaving(true)
    try {
      const isNew = !currentWorkflow.id
      const res = isNew
        ? await workflowService.create(currentWorkflow)
        : await workflowService.update(currentWorkflow.id, currentWorkflow)

      if (res.success) {
        await fetchWorkflows()
        if (isNew) {
          setSelectedId(res.data.id)
          setCurrentWorkflow(res.data)
        }
        message.success(t('common.success.saved'))
      }
    } catch (error) {
      message.error(t('common.error.save_failed'))
    } finally {
      setSaving(false)
    }
  }

  // Delete workflow
  const handleDelete = async () => {
    if (!currentWorkflow) return
    try {
      const res = await workflowService.delete(currentWorkflow.id)
      if (res.success) {
        setSelectedId(null)
        setCurrentWorkflow(null)
        await fetchWorkflows()
        message.success(t('common.success.deleted'))
      }
    } catch (error) {
      message.error(t('common.error.delete_failed'))
    }
  }

  // AI Assistant handler
  const handleAiSendMessage = async (userMessage, chatHistory) => {
    setAiProcessing(true)
    try {
      const res = await workflowService.aiChat({
        current_script: currentWorkflow?.script || '',
        current_ui_config: currentWorkflow?.ui_config || {},
        user_message: userMessage,
        chat_history: chatHistory
      })

      if (res.success) {
        // Update script and ui_config
        setCurrentWorkflow(prev => ({
          ...prev,
          script: res.data.modified_script || prev.script,
          ui_config: res.data.modified_ui_config || prev.ui_config
        }))
        return res.data.ai_response
      } else {
        message.error(res.message || t('workflows.ai_assistant.error'))
        throw new Error(res.message)
      }
    } catch (error) {
      console.error('AI Assistant error:', error)
      throw error
    } finally {
      setAiProcessing(false)
    }
  }

  // Update workflow config
  const handleConfigChange = (field, value) => {
    setCurrentWorkflow(prev => ({
      ...prev,
      [field]: value
    }))
  }

  const selectOptions = [
    { value: '__new__', label: `+ ${t('workflows.new_workflow')}` },
    ...workflows.map(w => ({ value: w.id, label: w.name }))
  ]

  const handleSelectChange = (value) => {
    if (value === '__new__') {
      handleCreate()
    } else {
      setSelectedId(value)
    }
  }

  return (
    <div>
      <ListPageHeader
        basePath="/admin"
        items={['workflows']}
      />
      {/* Header with Select */}
      <div style={{ marginBottom: 16 }}>
        <Space>
          <Select
            style={{ width: 300 }}
            placeholder={t('workflows.select_workflow')}
            value={selectedId}
            onChange={handleSelectChange}
            options={selectOptions}
            loading={loading}
          />
          {currentWorkflow && (
            <>
              <Input
                style={{ width: 200 }}
                placeholder={t('workflows.name')}
                value={currentWorkflow.name}
                onChange={(e) => handleConfigChange('name', e.target.value)}
              />
              <Button
                type="primary"
                icon={<SaveOutlined />}
                onClick={handleSave}
                loading={saving}
              >
                {t('common.save')}
              </Button>
              <Button
                danger
                icon={<DeleteOutlined />}
                onClick={handleDelete}
              >
                {t('common.delete')}
              </Button>
            </>
          )}
        </Space>
      </div>

      {/* Main Content */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: 50 }}><Spin /></div>
      ) : currentWorkflow ? (
        <Row gutter={16}>
          {/* Left: Script Editor */}
          <Col span={12}>
            <Card
              title={t('workflows.script')}
              size="small"
              extra={
                <Button
                  type="primary"
                  icon={<RobotOutlined />}
                  size="small"
                  onClick={() => setAiDrawerVisible(true)}
                  ghost
                >
                  {t('workflows.ai_assistant.button')}
                </Button>
              }
            >
              <Input.TextArea
                value={currentWorkflow.script || ''}
                onChange={(e) => handleConfigChange('script', e.target.value)}
                rows={16}
                style={{ fontFamily: 'monospace', fontSize: 12 }}
                placeholder="// Write your script here..."
              />
              <div style={{ marginTop: 16 }}>
                <div style={{ fontWeight: 'bold', marginBottom: 8 }}>{t('workflows.ui_config')}</div>
                <CodeEditor
                  value={currentWorkflow.ui_config}
                  onChange={(val) => handleConfigChange('ui_config', val)}
                />
              </div>
            </Card>
          </Col>

          {/* Right: Execution Panel */}
          <Col span={12}>
            <Card title={t('workflows.execution')} size="small">
              <ExecutionPanel
                workflow={currentWorkflow}
                services={workflowServices}
              />
            </Card>
          </Col>
        </Row>
      ) : (
        <Card>
          <Empty description={t('workflows.select_or_create')} />
        </Card>
      )}

      {/* AI Assistant Drawer */}
      <AIAssistantDrawer
        visible={aiDrawerVisible}
        onClose={() => setAiDrawerVisible(false)}
        currentScript={currentWorkflow?.script}
        currentUiConfig={currentWorkflow?.ui_config}
        processing={aiProcessing}
        onSendMessage={handleAiSendMessage}
      />
    </div>
  )
}
